import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Calendar, Clock, User, MapPin, ChevronDown, Users, RefreshCw, Home, Bed, Building2, CheckCircle, XCircle, Clock12 } from 'lucide-react';
import { Room } from '../../../types/accommodation';
import { useLanguage } from '../../../contexts/LanguageContext';
import { usePackagesStore } from '../../../stores/packagesStore';
import { usePilgrimGuideStore } from '../../../stores/pilgrimGuideStore';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { motion } from 'framer-motion';

type Props = {
  table: any;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  roomType: string;
  onRoomTypeChange: (value: string) => void;
  occupancyStatus: string;
  onOccupancyStatusChange: (value: string) => void;
  packageFilter: string;
  onPackageFilterChange: (value: string) => void;
  checkInFilter: string;
  onCheckInFilterChange: (value: string) => void;
  checkOutFilter: string;
  onCheckOutFilterChange: (value: string) => void;
  availablePeriodStart: string;
  onAvailablePeriodStartChange: (value: string) => void;
  availablePeriodEnd: string;
  onAvailablePeriodEndChange: (value: string) => void;
  itemsPerPage: number;
  onItemsPerPageChange: (value: number) => void;
  rooms: Room[];
  showOnlyMyPilgrims: boolean;
  onShowOnlyMyPilgrimsChange: (value: boolean) => void;
  genderFilter: 'all' | 'male' | 'female';
  onGenderFilterChange: (value: 'all' | 'male' | 'female') => void;
  unaccommodatedPilgrims: any[];
  onPilgrimSelect: (pilgrim: any) => void;
};

export const RoomFilters = ({
  table,
  searchTerm,
  onSearchChange,
  roomType,
  onRoomTypeChange,
  occupancyStatus,
  onOccupancyStatusChange,
  packageFilter,
  onPackageFilterChange,
  checkInFilter,
  onCheckInFilterChange,
  checkOutFilter,
  onCheckOutFilterChange,
  availablePeriodStart,
  onAvailablePeriodStartChange,
  availablePeriodEnd,
  onAvailablePeriodEndChange,
  itemsPerPage,
  onItemsPerPageChange,
  rooms,
  showOnlyMyPilgrims,
  onShowOnlyMyPilgrimsChange,
  genderFilter,
  onGenderFilterChange,
  unaccommodatedPilgrims,
  onPilgrimSelect
}: Props) => {
  const { translations } = useLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedPilgrim, setSelectedPilgrim] = useState<any | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [selectorData, setSelectorData] = useState<any | null>(null);
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const { links } = usePilgrimGuideStore();
  const { packages, fetchPackages, loading: loadingPackages } = usePackagesStore();

  // Ensure packages are loaded
  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Package statistics
  const packageStats = React.useMemo(() => {
    const stats: Record<string, number> = {};
    
    if (userRole === 'guide') {
      Object.values(links).forEach(link => {
        if (link.guideId === userId) {
          stats[link.packageName] = (stats[link.packageName] || 0) + 1;
        }
      });
    } else if (userRole === 'organizer') {
      Object.values(links).forEach(link => {
        if (link.organizerId === userId) {
          stats[link.packageName] = (stats[link.packageName] || 0) + 1;
        }
      });
    }
    
    return stats;
  }, [links, userRole, userId]);

  // Create a mapping between package nicknames and package objects
  const packageMapping = React.useMemo(() => {
    const mapping: Record<string, any> = {};
    
    // First map by ID (the default keys)
    Object.entries(packages).forEach(([id, pkg]) => {
      mapping[id] = pkg;
    });
    
    // Then map by package name for easier lookup
    Object.values(packages).forEach(pkg => {
      if (pkg.name) {
        mapping[pkg.name] = pkg;
      }
    });
    
    return mapping;
  }, [packages]);

  // Filter pilgrims based on search text and hotel eligibility
  const filteredPilgrims = React.useMemo(() => {
    console.log(`Filtering pilgrims for hotel: ${table.hn}`);
    console.log(`Total unaccommodated pilgrims before filtering: ${unaccommodatedPilgrims?.length || 0}`);
    
    if (!unaccommodatedPilgrims || !Array.isArray(unaccommodatedPilgrims)) {
      console.log('No unaccommodated pilgrims data available');
      return [];
    }
    
    if (loadingPackages) {
      console.log('Packages still loading, waiting for data...');
      return [];
    }
    
    // Filter pilgrims who should be in this specific hotel according to their package
    const hotelEligiblePilgrims = unaccommodatedPilgrims.filter(pilgrim => {
      // Basic search text filter
      if (filterText && !pilgrim.name?.toLowerCase().includes(filterText.toLowerCase()) && 
          !pilgrim.passportNumber?.toLowerCase().includes(filterText.toLowerCase())) {
        return false;
      }
      
      // Check if the pilgrim has a valid package name
      if (!pilgrim.packageName) {
        console.log(`Pilgrim ${pilgrim.name} filtered out: No package name`);
        return false;
      }
      
      // Try to find the package by name or package ID
      const pilgrimPackage = packageMapping[pilgrim.packageName];
      if (!pilgrimPackage) {
        console.log(`Pilgrim ${pilgrim.name} filtered out: Package not found for "${pilgrim.packageName}"`);
        return false;
      }
      
      // Check if pilgrim should be staying in this hotel based on package
      const isMainMakkahMatch = pilgrimPackage.mainMakkahHotel === table.hn;
      const isShiftingMakkahMatch = pilgrimPackage.shiftingMakkahHotel === table.hn;
      const isMadinahMatch = pilgrimPackage.madinahHotel === table.hn;
      
      const isEligibleForHotel = isMainMakkahMatch || isShiftingMakkahMatch || isMadinahMatch;
      
      if (!isEligibleForHotel) {
        console.log(`Pilgrim ${pilgrim.name} filtered out: Not eligible for hotel ${table.hn}`);
        return false;
      }

      // Now check room type matching if specified
      if (roomType) {
        let relevantRoomType: string | undefined;
        
        if (isMainMakkahMatch) {
          relevantRoomType = pilgrim.main_makkah_roomType;
        } else if (isShiftingMakkahMatch) {
          relevantRoomType = pilgrim.shifting_makkah_roomType;
        } else if (isMadinahMatch) {
          relevantRoomType = pilgrim.madinah_roomType;
        }
        
        // If room type is selected in filter, check if it matches
        if (relevantRoomType) {
          const roomTypeMap: Record<string, string> = {
            'd': 'double',
            't': 'triple',
            'q': 'quadruple'
          };
          
          const expectedRoomType = roomTypeMap[roomType];
          const roomTypeMatches = relevantRoomType === expectedRoomType;
          
          if (!roomTypeMatches) {
            console.log(`Pilgrim ${pilgrim.name} filtered out: Room type mismatch - has ${relevantRoomType}, expected ${expectedRoomType}`);
            return false;
          }
        }
      }
      
      console.log(`Pilgrim ${pilgrim.name} INCLUDED in filtered results for hotel ${table.hn}`);
      return true;
    });
    
    console.log(`Hotel-eligible pilgrims after filtering: ${hotelEligiblePilgrims.length}`);
    return hotelEligiblePilgrims;
  }, [unaccommodatedPilgrims, filterText, table.hn, roomType, packageMapping, loadingPackages]);

  const handlePilgrimSelect = async (pilgrim: any) => {
    try {
      console.log(`Selecting pilgrim: ${pilgrim.name}, ID: ${pilgrim.id}`);
      
      // Get PilgrimSelector data
      const selectorRef = collection(firestore, 'PilgrimSelector');
      const selectorQuery = query(selectorRef, where('pilgrimId', '==', pilgrim.id));
      const selectorSnapshot = await getDocs(selectorQuery);

      if (!selectorSnapshot.empty) {
        const selectorDoc = selectorSnapshot.docs[0];
        const selectorData = selectorDoc.data();
        console.log('Found selector data for pilgrim:', selectorData);

        // Get dates based on hotel
        let startDate = '';
        let endDate = '';
        
        console.log(`Getting dates for hotel: ${table.hn} from package data`);

        if (table.hn === selectorData.packageData.mainMakkah.hotel) {
          console.log('Using main Makkah dates');
          startDate = selectorData.packageData.mainMakkah.startDate;
          endDate = selectorData.packageData.mainMakkah.endDate;
        } else if (selectorData.packageData.shiftingMakkah && 
                   table.hn === selectorData.packageData.shiftingMakkah.hotel) {
          console.log('Using shifting Makkah dates');
          startDate = selectorData.packageData.shiftingMakkah.startDate;
          endDate = selectorData.packageData.shiftingMakkah.endDate;
        } else if (table.hn === selectorData.packageData.madinah.hotel) {
          console.log('Using Madinah dates');
          startDate = selectorData.packageData.madinah.startDate;
          endDate = selectorData.packageData.madinah.endDate;
        }

        console.log('Original date strings from selector data:', { startDate, endDate });

        // Convert dd/mm/yyyy to yyyy-mm-dd for input[type="date"]
        const convertDate = (dateStr: string) => {
          if (!dateStr) {
            console.log('Empty date string, returning empty value');
            return '';
          }
          
          const [day, month, year] = dateStr.split('/');
          
          if (!day || !month || !year) {
            console.log('Invalid date format, returning empty value');
            return '';
          }
          
          const formatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          console.log(`Converted ${dateStr} to ${formatted}`);
          return formatted;
        };

        // Set the dates
        const formattedStartDate = convertDate(startDate);
        const formattedEndDate = convertDate(endDate);
        
        console.log('Setting available period dates from pilgrim selection:', {
          originalStartDate: startDate,
          originalEndDate: endDate,
          formattedStartDate,
          formattedEndDate
        });
        
        onAvailablePeriodStartChange(formattedStartDate);
        onAvailablePeriodEndChange(formattedEndDate);

        // Store selector data
        setSelectorData(selectorData);
      } else {
        console.log('No selector data found for pilgrim');
      }

      setSelectedPilgrim(pilgrim);
      onPilgrimSelect(pilgrim);
      setIsDropdownOpen(false);
      setFilterText('');
    } catch (error) {
      console.error('Error getting selector data:', error);
    }
  };

  // Handle mutual exclusivity between filters
  const handleShowOnlyMyPilgrimsChange = (value: boolean) => {
    if (value) {
      // Reset available period and selected pilgrim when selecting "my pilgrims"
      onAvailablePeriodStartChange('');
      onAvailablePeriodEndChange('');
      setSelectedPilgrim(null);
      setSelectorData(null);
    }
    onShowOnlyMyPilgrimsChange(value);
  };

  const handleAvailablePeriodChange = (start: string, end: string) => {
    if (start || end) {
      // Reset "my pilgrims" when selecting available period
      onShowOnlyMyPilgrimsChange(false);
    }
    onAvailablePeriodStartChange(start);
    onAvailablePeriodEndChange(end);
  };

  // Reset all filters
  const resetFilters = () => {
    onSearchChange('');
    onRoomTypeChange('');
    onOccupancyStatusChange('');
    onPackageFilterChange('');
    onCheckInFilterChange('');
    onCheckOutFilterChange('');
    onAvailablePeriodStartChange('');
    onAvailablePeriodEndChange('');
    onShowOnlyMyPilgrimsChange(false);
    onGenderFilterChange('all');
    setSelectedPilgrim(null);
    setFilterText('');
    setSelectorData(null);
  };

  // Room Type Options with icons
  const roomTypeOptions = [
    { value: '', label: translations?.roomFilters?.options?.allRoomTypes || 'All Room Types', icon: Home },
    { value: 'd', label: translations?.roomFilters?.options?.double || 'Double', icon: Bed },
    { value: 't', label: translations?.roomFilters?.options?.triple || 'Triple', icon: Bed },
    { value: 'q', label: translations?.roomFilters?.options?.quadruple || 'Quadruple', icon: Bed }
  ];

  // Status Options with icons
  const statusOptions = [
    { value: '', label: translations?.roomFilters?.options?.allStatuses || 'All Statuses', icon: Clock12 },
    { value: 'available', label: translations?.roomFilters?.options?.available || 'Available', icon: CheckCircle },
    { value: 'partial', label: translations?.roomFilters?.options?.partiallyOccupied || 'Partially Occupied', icon: Clock },
    { value: 'occupied', label: translations?.roomFilters?.options?.fullyOccupied || 'Fully Occupied', icon: XCircle }
  ];

  // Common styling
  const inputClassName = "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white";
  const selectClassName = "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white";

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm space-y-6">
      {/* First row - Search and Package filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder={translations?.roomFilters?.placeholders?.search || 'Search...'}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`${inputClassName} pl-10 pr-4 text-right`}
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        </div>

        {/* Package Filter */}
        <div>
          <select
            value={packageFilter}
            onChange={(e) => onPackageFilterChange(e.target.value)}
            className={selectClassName}
          >
            <option value="">
              {translations?.roomFilters?.options?.allPackages || 'All Packages'}
            </option>
            {Object.entries(packageStats)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([packageName, count]) => (
                <option key={packageName} value={packageName}>
                  {packageName} ({count})
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Room Type Radio Buttons - Stylized with animations */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {translations?.roomFilters?.options?.roomTypes || 'Room Type'}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {roomTypeOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = roomType === option.value;
            
            return (
              <motion.div
                key={option.value}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onRoomTypeChange(option.value)}
                className={`
                  relative p-3 rounded-xl cursor-pointer border-2 transition-all duration-300
                  ${isSelected 
                    ? 'border-primary-500 bg-primary-50 shadow-sm' 
                    : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className={`
                    p-2 rounded-full 
                    ${isSelected ? 'bg-primary-200 text-primary-700' : 'bg-gray-100 text-gray-600'}
                  `}>
                    <Icon size={option.value ? 18 : 20} className="transition-colors duration-300" />
                  </div>
                  <span className={`
                    text-sm font-medium transition-colors duration-300
                    ${isSelected ? 'text-primary-700' : 'text-gray-600'}
                  `}>
                    {option.label}
                  </span>
                  
                  {option.value && option.value !== '' && (
                    <div className="absolute top-2 right-2 flex items-center justify-center">
                      <motion.div 
                        initial={false}
                        animate={{ 
                          scale: isSelected ? 1 : 0,
                          opacity: isSelected ? 1 : 0
                        }}
                        className="w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center"
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2 h-2 bg-white rounded-full"
                          />
                        )}
                      </motion.div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Room Status Radio Buttons - Stylized with animations */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {translations?.roomFilters?.options?.status || 'Room Status'}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {statusOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = occupancyStatus === option.value;
            
            // Define color based on status type
            let bgColor = isSelected ? 'bg-primary-50' : 'bg-white';
            let iconBgColor = 'bg-gray-100';
            let iconColor = 'text-gray-600';
            let borderColor = isSelected ? 'border-primary-500' : 'border-gray-200';
            
            if (isSelected) {
              if (option.value === 'available') {
                iconBgColor = 'bg-green-100';
                iconColor = 'text-green-600';
              } else if (option.value === 'partial') {
                iconBgColor = 'bg-yellow-100';
                iconColor = 'text-yellow-600';
              } else if (option.value === 'occupied') {
                iconBgColor = 'bg-red-100';
                iconColor = 'text-red-600';
              }
            }
            
            return (
              <motion.div
                key={option.value}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onOccupancyStatusChange(option.value)}
                className={`
                  relative p-3 rounded-xl cursor-pointer border-2 transition-all duration-300
                  ${borderColor} ${bgColor} ${isSelected ? 'shadow-sm' : ''}
                  hover:border-primary-300 hover:bg-gray-50
                `}
              >
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className={`
                    p-2 rounded-full transition-colors duration-300
                    ${isSelected ? iconBgColor : 'bg-gray-100'}
                  `}>
                    <Icon size={18} className={`
                      transition-colors duration-300
                      ${isSelected ? iconColor : 'text-gray-500'}
                    `} />
                  </div>
                  <span className={`
                    text-sm font-medium transition-colors duration-300
                    ${isSelected ? 'text-primary-700' : 'text-gray-600'}
                  `}>
                    {option.label}
                  </span>
                  
                  {option.value && option.value !== '' && (
                    <div className="absolute top-2 right-2 flex items-center justify-center">
                      <motion.div 
                        initial={false}
                        animate={{ 
                          scale: isSelected ? 1 : 0,
                          opacity: isSelected ? 1 : 0
                        }}
                        className="w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center"
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2 h-2 bg-white rounded-full"
                          />
                        )}
                      </motion.div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Items per page */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {translations?.roomFilters?.itemsPerPage || 'Items Per Page'}
        </label>
        <div className="flex gap-3">
          {[10, 50, 100].map(size => (
            <motion.button
              key={size}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onItemsPerPageChange(size)}
              className={`
                px-4 py-2 rounded-lg border-2 transition-all duration-200
                ${itemsPerPage === size 
                  ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium shadow-sm' 
                  : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:bg-gray-50'}
              `}
            >
              {size}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Available period section */}
      <div className="bg-primary-50 p-6 rounded-xl border border-primary-100 shadow-sm">
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-medium text-primary-700 mb-1">
              {translations?.roomFilters?.labels?.availablePeriod || 'Find Available Period'}
            </h3>
            <p className="text-sm text-primary-600">
              {translations?.roomFilters?.availablePeriodDescription || 
                'Show rooms that have at least one bed free during the specified period'}
            </p>
          </div>

          {/* Unaccommodated Pilgrims Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 transition-all duration-200"
            >
              {selectedPilgrim ? (
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${
                    selectedPilgrim.gender === 'male' ? 'bg-blue-50' : 'bg-pink-50'
                  }`}>
                    <User className={
                      selectedPilgrim.gender === 'male' ? 'text-blue-500' : 'text-pink-500'
                    } size={18} />
                  </div>
                  <div>
                    <div className="font-medium">{selectedPilgrim.name}</div>
                    <div className="text-sm text-gray-500">
                      {translations?.roomFilters?.passport || 'Passport'}: {selectedPilgrim.passportNumber}
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-gray-500">
                  {translations?.roomFilters?.selectPilgrim || 'Select an unaccommodated pilgrim'}
                </span>
              )}
              <ChevronDown 
                size={20} 
                className={`text-gray-400 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} 
              />
            </button>

            {/* Dropdown Content - Positioned absolutely with high z-index */}
            {isDropdownOpen && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-300 rounded-lg shadow-xl"
                style={{top: 'calc(100% + 4px)'}}
              >
                {/* Search Input */}
                <div className="p-3 border-b border-gray-200">
                  <div className="relative">
                    <input
                      type="text"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      placeholder={translations?.roomFilters?.searchPilgrimPlaceholder || "Filter pilgrims..."}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white"
                    />
                    <Search 
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" 
                      size={16} 
                    />
                  </div>
                </div>

                {/* Results - Fixed height with scroll */}
                <div className="max-h-48 overflow-y-auto border-gray-100">
                  {filteredPilgrims && filteredPilgrims.length > 0 ? (
                    filteredPilgrims.map((pilgrim, index) => (
                      <motion.div
                        key={`pilgrim-${pilgrim.id || index}`}
                        whileHover={{ backgroundColor: 'rgba(236, 239, 241, 0.6)' }}
                        onClick={() => handlePilgrimSelect(pilgrim)}
                        className="p-3 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${
                            pilgrim.gender === 'male' ? 'bg-blue-50' : 'bg-pink-50'
                          }`}>
                            <User className={
                              pilgrim.gender === 'male' ? 'text-blue-500' : 'text-pink-500'
                            } size={18} />
                          </div>
                          <div>
                            <div className="font-medium">{pilgrim.name}</div>
                            <div className="text-sm text-gray-500">
                              {translations?.roomFilters?.passport || 'Passport'}: {pilgrim.passportNumber}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      {filterText ? (
                        translations?.roomFilters?.noResults || 'No pilgrims found'
                      ) : loadingPackages ? (
                        'Loading pilgrims...'
                      ) : (
                        translations?.roomFilters?.noUnaccommodated || 'No unaccommodated pilgrims for this hotel'
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-primary-700 mb-1">
                {translations?.roomFilters?.labels?.checkIn || 'Check-in'}
              </label>
              <input
                type="date"
                value={availablePeriodStart}
                onChange={(e) => handleAvailablePeriodChange(e.target.value, availablePeriodEnd)}
                className={`${inputClassName} hover:border-primary-300 focus:border-primary-500 transition-colors duration-200`}
              />
            </div>
            <div>
              <label className="block text-sm text-primary-700 mb-1">
                {translations?.roomFilters?.labels?.checkOut || 'Check-out'}
              </label>
              <input
                type="date"
                value={availablePeriodEnd}
                onChange={(e) => handleAvailablePeriodChange(availablePeriodStart, e.target.value)}
                className={`${inputClassName} hover:border-primary-300 focus:border-primary-500 transition-colors duration-200`}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        {/* My Pilgrims Checkbox */}
        {(userRole === 'guide' || userRole === 'organizer') && (
          <label className="flex items-center gap-2 cursor-pointer group">
            <div className="relative flex items-center justify-center">
              <input
                type="checkbox"
                checked={showOnlyMyPilgrims}
                onChange={(e) => handleShowOnlyMyPilgrimsChange(e.target.checked)}
                className="opacity-0 absolute h-6 w-6 cursor-pointer"
              />
              <div className={`
                w-6 h-6 border-2 rounded transition-colors duration-200
                ${showOnlyMyPilgrims 
                  ? 'bg-primary-500 border-transparent' 
                  : 'bg-white border-gray-300 group-hover:border-primary-300'}
              `}>
                {showOnlyMyPilgrims && (
                  <motion.svg
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full h-full text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={3}
                      d="M5 13l4 4L19 7" 
                    />
                  </motion.svg>
                )}
              </div>
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-primary-700 transition-colors duration-200">
              {translations?.roomFilters?.myPilgrims || 'Only My Pilgrims'}
            </span>
          </label>
        )}

        {/* Reset Filters Button */}
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={resetFilters}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw size={16} />
          <span>{translations?.roomFilters?.resetFilters || 'Reset Filters'}</span>
        </motion.button>
      </div>

      {/* Active Filters */}
      {(searchTerm || roomType || occupancyStatus || packageFilter || checkInFilter || 
        checkOutFilter || availablePeriodStart || availablePeriodEnd || showOnlyMyPilgrims || 
        selectedPilgrim) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg"
        >
          <Filter size={16} className="text-primary-500" />
          <span className="text-sm text-gray-600">
            {translations?.roomFilters?.activeFilters || 'Active Filters:'}
          </span>
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md shadow-sm"
              >
                {translations?.roomFilters?.search || 'Search'}: {searchTerm}
              </motion.span>
            )}
            {roomType && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md shadow-sm"
              >
                {translations?.roomFilters?.roomType || 'Room Type'}: {
                  roomType === 'd' ? (translations?.roomFilters?.options?.double || 'Double') :
                  roomType === 't' ? (translations?.roomFilters?.options?.triple || 'Triple') :
                  translations?.roomFilters?.options?.quadruple || 'Quadruple'
                }
              </motion.span>
            )}
            {occupancyStatus && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md shadow-sm"
              >
                {translations?.roomFilters?.status || 'Status'}: {
                  occupancyStatus === 'available' ? (translations?.roomFilters?.options?.available || 'Available') :
                  occupancyStatus === 'partial' ? (translations?.roomFilters?.options?.partiallyOccupied || 'Partially Occupied') :
                  translations?.roomFilters?.options?.fullyOccupied || 'Fully Occupied'
                }
              </motion.span>
            )}
            {packageFilter && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md shadow-sm"
              >
                {translations?.roomFilters?.package || 'Package'}: {packageFilter}
              </motion.span>
            )}
            {selectedPilgrim && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md shadow-sm flex items-center gap-1"
              >
                <User size={12} />
                {selectedPilgrim.name} ({selectedPilgrim.passportNumber})
              </motion.span>
            )}
            {(availablePeriodStart && availablePeriodEnd) && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md shadow-sm"
              >
                {translations?.roomFilters?.availablePeriod || 'Available Period'}: {availablePeriodStart} - {availablePeriodEnd}
              </motion.span>
            )}
            {showOnlyMyPilgrims && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md shadow-sm"
              >
                {translations?.roomFilters?.myPilgrims || 'My Pilgrims Only'}
              </motion.span>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};
